// @ts-nocheck
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react'
import ProductReviewRating from '@components/product/product-details/product-review-rating'
import Carousel from '@components/ui/carousel/carousel'
import { ProductPageContext } from '@contexts/product-page'
import { useStore } from '@contexts/store'
import { useProductGuides } from '@framework/get-product-guides'
import { Guide } from '@framework/model-types/Product'
import { Sanitized } from '@helper/globalHelpers'
import { DisplayUploadCareAsset } from '@hooks/useUploadCare'
import { PRODUCT_DETAILS_SECTION_ID, ROUTES } from '@utils/routes'
import isEmpty from 'lodash/isEmpty'
import { Fragment, useContext, useMemo } from 'react'
import { Element } from 'react-scroll'
import { SwiperSlide } from 'swiper/react'
import ProductSpecifications from './product-specifications'

const ProductDetails = () => {
  const { product } = useContext(ProductPageContext)
  return (
    <Element name={PRODUCT_DETAILS_SECTION_ID}>
      <Box
        id={PRODUCT_DETAILS_SECTION_ID}
        scrollMarginTop="10rem"
        w={'100%'}
        className="text-sm sm:text-15px text-skin-muted leading-[2em] space-y-4 lg:space-y-5 xl:space-y-7"
      >
        <Sanitized html={product?.description} />
      </Box>
    </Element>
  )
}

const ProductGuide = ({ guides }: { guides: Guide[] }) => {
  return (
    <Carousel
      breakpoints={{
        '1536': {
          slidesPerView: 3.2,
        },
        '1280': {
          slidesPerView: 2.2,
        },
        '1024': {
          slidesPerView: 1.5,
        },
        '640': {
          slidesPerView: 1,
        },
        '360': {
          slidesPerView: 1,
        },
        '0': {
          slidesPerView: 1,
        },
      }}
      prevActivateId="prev-guide"
      nextActivateId="next-guide"
      nextButtonStyleProps={{
        boxShadow: 'md',
        'aria-label': 'Previous Image',
        bg: 'white',
        width: { base: '40px', lg: '60px' },
        height: { base: '40px', lg: '60px' },
        color: 'gray.700',
        _hover: {
          color: 'white',
          bg: 'brand',
        },
        position: 'absolute',
        right: '2%',
        iconProps: {
          size: 25,
        },
      }}
      prevButtonStyleProps={{
        boxShadow: 'md',
        'aria-label': 'Next Image',
        bg: 'white',
        width: { base: '40px', lg: '60px' },
        height: { base: '40px', lg: '60px' },
        color: 'gray.700',
        _hover: {
          color: 'white',
          bg: 'brand',
        },
        position: 'absolute',
        left: '2%',
        iconProps: {
          size: 25,
        },
      }}
    >
      {guides?.map((guide, index) => {
        const { _id, name, contentType, text, media } = guide
        return (
          <SwiperSlide key={`${_id}-${name}-${index}`}>
            <Flex direction="column" align={{ base: 'center', md: 'start' }}>
              <Text
                mb="3.5"
                width="full"
                fontSize="20px"
                align="center"
                fontWeight="600"
              >
                {name}
              </Text>
              {contentType === 'image' ? (
                <DisplayUploadCareAsset
                  src={media?.uuid ?? ''}
                  options={{
                    resize: 'x1080',
                    quality: 'smart_retina',
                  }}
                  dimensions={{ height: [300, 400] }}
                  alt={`${name} Guide`}
                  className="object-contain bg-skin-thumbnail"
                />
              ) : null}
              {contentType === 'rich-text-area' ? (
                <Sanitized html={text} />
              ) : null}
            </Flex>
          </SwiperSlide>
        )
      })}
    </Carousel>
  )
}

const ProductDetailsTab = () => {
  const { storeTemplate, storeData } = useStore()
  const { showProductSpecs, product } = useContext(ProductPageContext)
  const { guides: globalGuides } = useProductGuides(storeData?._id)

  const { guides: specificGuides } = product

  const allGuides = useMemo(() => {
    if (storeData?.plan == 'free') {
      return []
    }
    return [
      ...(globalGuides?.filter(
        (guide) =>
          !guide?.disabled && guide.productOptions !== 'specific-products',
      ) ?? []),
      ...(specificGuides?.filter((guide) => !guide?.disabled) ?? []),
    ]
  }, [globalGuides, specificGuides, storeData?.plan])

  const sections = useMemo(
    () =>
      [
        {
          title: 'Product Details',
          itemId: ROUTES.PRODUCT_DETAILS_SECTION_ID,
          content: storeTemplate !== 'RETRO' ? <ProductDetails /> : null,
        },
        {
          title: 'Product Guide',
          itemId: ROUTES.GUIDES_SECTION_ID,
          content: !isEmpty(allGuides) ? (
            <ProductGuide guides={allGuides} />
          ) : null,
        },
        {
          title: 'Product Specifications',
          content: showProductSpecs ? <ProductSpecifications /> : null,
          buttonWrapper: Element,
          wrapperProps: { name: ROUTES.PRODUCT_SPECIFICATIONS },
        },
        {
          title: 'Reviews',
          content: <ProductReviewRating />,
          itemId: ROUTES.REVIEWS_SECTION_ID,
        },
      ].filter((section) => section.content),
    [allGuides, showProductSpecs, storeTemplate],
  )

  return (
    <Box py={{ base: '7', lg: '10' }} className="w-full xl:px-2 sm:px-0">
      <Accordion
        defaultIndex={Array.from({ length: sections.length }).map((_, index) => index)}
        allowMultiple
      >
        {sections?.map(
          ({ title, content, itemId, buttonWrapper, wrapperProps }, index) => {
            const ButtonWrapper = buttonWrapper ?? Fragment
            return (
              <AccordionItem key={title} border="none">
                <ButtonWrapper {...(buttonWrapper ? wrapperProps : {})}>
                  <AccordionButton
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px="0"
                    my="4"
                    _hover={{ bg: 'transparent' }}
                    borderBottom="1px solid"
                    borderColor="inputFieldOutline"
                  >
                    <Heading
                      className="font-semibold leading-1.2"
                      as="h4"
                      id={itemId}
                      scrollMarginTop={itemId ? '150px' : undefined}
                      size="md"
                    >
                      {title}
                    </Heading>
                    <AccordionIcon />
                  </AccordionButton>
                </ButtonWrapper>
                <AccordionPanel pb={4} px="0">
                  {content}
                </AccordionPanel>
              </AccordionItem>
            )
          },
        )}
      </Accordion>
    </Box>
  )
}

export default ProductDetailsTab
